import { useState, useEffect, useMemo } from 'react';
import { FilterOption } from '@/components/ui/FilterDropdown';
import { ImageType } from '@/components/image-generator/FilterBar/TypeFilter';
import { 
  useStaticStylePrompts, 
  useStaticInfluencePrompts, 
  useStaticColorPrompts, 
  useStaticBackgroundPrompts, 
  useStaticEnvironmentPrompts,
  useStaticMoodPrompts
} from '../static-filters/hooks';
import { ImageFiltersLoading, ImageFiltersState } from './types';

// Helper function to convert prompt library items to filter options
const convertToFilterOptions = (
  prompts: any[] | undefined, 
  selectedId?: string
): FilterOption[] => {
  if (!prompts || prompts.length === 0) return [];
  
  return prompts.map(prompt => ({
    id: prompt.id,
    label: prompt.prompt_name,
    checked: prompt.id === selectedId
  }));
};

export const useFilterData = (
  selectedType: ImageType | null,
  selectedStyle: string,
  selectedInfluence: string,
  selectedBackground: string
): {
  filterOptions: Pick<ImageFiltersState, 
    'styleOptions' | 
    'influenceOptions' | 
    'colorOptions' | 
    'backgroundOptions' | 
    'environmentOptions' | 
    'moodOptions'
  >;
  setFilterOptions: {
    setStyleOptions: (options: FilterOption[]) => void;
    setInfluenceOptions: (options: FilterOption[]) => void;
    setColorOptions: (options: FilterOption[]) => void;
    setBackgroundOptions: (options: FilterOption[]) => void;
    setEnvironmentOptions: (options: FilterOption[]) => void;
    setMoodOptions: (options: FilterOption[]) => void;
  };
  isLoading: ImageFiltersLoading;
} => {
  // Filter options state
  const [styleOptions, setStyleOptions] = useState<FilterOption[]>([]);
  const [influenceOptions, setInfluenceOptions] = useState<FilterOption[]>([]);
  const [colorOptions, setColorOptions] = useState<FilterOption[]>([]);
  const [backgroundOptions, setBackgroundOptions] = useState<FilterOption[]>([]);
  const [environmentOptions, setEnvironmentOptions] = useState<FilterOption[]>([]);
  const [moodOptions, setMoodOptions] = useState<FilterOption[]>([]);
  
  // Fetch data from static hooks instead of database
  const { data: styleData, isLoading: styleLoading } = useStaticStylePrompts(selectedType);
  const { data: influenceData, isLoading: influenceLoading } = useStaticInfluencePrompts(selectedType);
  const { data: colorData, isLoading: colorLoading } = useStaticColorPrompts();
  const { data: backgroundData, isLoading: backgroundLoading } = useStaticBackgroundPrompts();
  const { data: environmentData, isLoading: environmentLoading } = useStaticEnvironmentPrompts();
  const { data: moodData, isLoading: moodLoading } = useStaticMoodPrompts();

  // Memoize expensive filter option conversions
  const memoizedStyleOptions = useMemo(() => {
    return styleData ? convertToFilterOptions(styleData, selectedStyle) : [];
  }, [styleData, selectedStyle]);

  const memoizedInfluenceOptions = useMemo(() => {
    return influenceData ? convertToFilterOptions(influenceData, selectedInfluence) : [];
  }, [influenceData, selectedInfluence]);

  const memoizedColorOptions = useMemo(() => {
    return colorData ? convertToFilterOptions(colorData) : [];
  }, [colorData]);

  const memoizedBackgroundOptions = useMemo(() => {
    return backgroundData ? convertToFilterOptions(backgroundData, selectedBackground) : [];
  }, [backgroundData, selectedBackground]);

  const memoizedEnvironmentOptions = useMemo(() => {
    return environmentData ? convertToFilterOptions(environmentData) : [];
  }, [environmentData]);

  const memoizedMoodOptions = useMemo(() => {
    return moodData ? convertToFilterOptions(moodData) : [];
  }, [moodData]);

  // Update style options when memoized data changes
  useEffect(() => {
    setStyleOptions(memoizedStyleOptions);
  }, [memoizedStyleOptions]);
  
  // Update influence options when memoized data changes
  useEffect(() => {
    setInfluenceOptions(memoizedInfluenceOptions);
  }, [memoizedInfluenceOptions]);
  
  // Update color options when memoized data changes
  useEffect(() => {
    setColorOptions(memoizedColorOptions);
  }, [memoizedColorOptions]);
  
  // Update background options when memoized data changes
  useEffect(() => {
    setBackgroundOptions(memoizedBackgroundOptions);
  }, [memoizedBackgroundOptions]);
  
  // Update environment options when memoized data changes
  useEffect(() => {
    setEnvironmentOptions(memoizedEnvironmentOptions);
  }, [memoizedEnvironmentOptions]);
  
  // Update mood options when memoized data changes
  useEffect(() => {
    setMoodOptions(memoizedMoodOptions);
  }, [memoizedMoodOptions]);

  return {
    filterOptions: {
      styleOptions,
      influenceOptions,
      colorOptions,
      backgroundOptions,
      environmentOptions,
      moodOptions,
    },
    setFilterOptions: {
      setStyleOptions,
      setInfluenceOptions,
      setColorOptions,
      setBackgroundOptions,
      setEnvironmentOptions,
      setMoodOptions,
    },
    isLoading: {
      style: styleLoading,
      influence: influenceLoading,
      color: colorLoading,
      background: backgroundLoading,
      environment: environmentLoading,
      mood: moodLoading,
    }
  };
};
